import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Popup } from 'components/Popup'
import { Button, BUTTON_TYPES } from 'components/Button'
import { TOption } from 'components/Select/types'
import { BTN_TXT } from 'constants/txt'
import { setLoading } from 'redux/store/common/slice'
import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import { useContractDetailsPopup } from 'features/ContractDetails/Providers/ContractDetailsPopupProvider'
import { updateRebatePeriodFacilities } from '../api'
import { UpdateRebatePeriodFacilitiesPayload } from '../types'
import {
  FacilityTiersTable,
  FacilityTiersTableHandle
} from './FacilityTiersTable'
import { FacilityTierRow } from './constants'
import { PeriodSelect } from './PeriodSelect'

type EditFacilityTiersPopupProps = {
  tierOptions: TOption[]
  periodOptions: TOption[]
  periodId?: string
  onPeriodChange: (value: string) => void
  rows: FacilityTierRow[]
  onSaved?: () => void
}

export const EditFacilityTiersPopup = ({
  tierOptions,
  periodOptions,
  periodId,
  onPeriodChange,
  rows,
  onSaved
}: EditFacilityTiersPopupProps) => {
  const dispatch = useDispatch()
  const tableRef = useRef<FacilityTiersTableHandle>(null)
  const [hasTierChanges, setHasTierChanges] = useState(false)
  const {
    state: { details }
  } = useContractDetailsContext()
  const {
    editFacilityTiersPopup: {
      state,
      actions: { close }
    }
  } = useContractDetailsPopup()

  useEffect(() => {
    if (!state.visible) setHasTierChanges(false)
  }, [state.visible])

  const saveFacilities = async (
    facilities: UpdateRebatePeriodFacilitiesPayload['facilities']
  ) => {
    const rebateId = details.rebate?.uuid

    if (!details.uuid || !rebateId || !periodId) return

    try {
      dispatch(setLoading(true))
      await updateRebatePeriodFacilities(details.uuid, rebateId, periodId, {
        facilities
      })
      onSaved?.()
      close()
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(setLoading(false))
    }
  }

  return (
    <Popup
      {...state}
      width={800}
      className="edit-facility-tiers-popup"
      footer={
        <>
          <Button
            type={BUTTON_TYPES.DEFAULT}
            onClick={close}
            upperCase
            htmlType="button"
          >
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            onClick={() => tableRef.current?.submit()}
            upperCase
            htmlType="button"
          >
            {BTN_TXT.SAVE}
          </Button>
        </>
      }
    >
      <div className="column gap-24">
        <PeriodSelect
          options={periodOptions}
          value={periodId}
          onChange={onPeriodChange}
          disabled={hasTierChanges}
        />
        <FacilityTiersTable
          ref={tableRef}
          selectable
          rows={rows}
          tierOptions={tierOptions}
          onSubmit={saveFacilities}
          onHasTierChanges={setHasTierChanges}
        />
      </div>
    </Popup>
  )
}
